import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Gradients } from "components/gradients";
import { useAnimatedValue } from "hooks/use-animated-value";
import { useFuelStatistics } from "hooks/use-fuel-statistics";
import { useStore } from "hooks/use-store";
import { useGauge } from "hooks/use-gauge";
import { Color } from "utils/theme";
import {
  MAIN_ARC_BG,
  MAIN_ARC_STROKE,
  OUTER_ARC_BG,
  OUTER_ARC_STROKE,
} from "components/gauge-style";
import { SvgGradients } from "types/enums";

// Constants
const START_ANGLE = 225;
const END_ANGLE = 315;
const DIAMETER = 450;
const ARC_WIDTH = 30;
const STROKE_CAP = "round";

export const FuelRateGauge = () => {
  const { t } = useTranslation("translation", { keyPrefix: "fuel_curve" });

  // Store values
  const config = useStore((state) => state.config);

  // Fuel statistics
  const {
    avgFuelRateNmi,
    displayFuelRateNmi,
    isFuelRateBad,
    fuelRateWarnThreshold,
    maxFuelRateInCurve,
  } = useFuelStatistics();

  // Animated value for smooth transitions
  const animatedFuelRate = useAnimatedValue(avgFuelRateNmi ?? 0);

  // Determine the maximum value for the gauge dynamically
  const gaugeMaxValue = useMemo(() => {
    const fuelRateLimit = (config?.target_fuel_rate || 0) * 1.2;
    return Math.max(10, fuelRateLimit, animatedFuelRate ?? 0, maxFuelRateInCurve);
  }, [animatedFuelRate, config, maxFuelRateInCurve]);

  // Gauge setup
  const gauge = useGauge({
    domain: [0, gaugeMaxValue],
    startAngle: START_ANGLE,
    endAngle: END_ANGLE,
    numTicks: 10,
    diameter: DIAMETER,
  });

  const limitAngle = gauge.valueToAngle(fuelRateWarnThreshold ?? gaugeMaxValue);
  const label = t("units.liters_per_nm");
  const value = avgFuelRateNmi ?? 0;

  return (
    <svg
      {...gauge.getSVGProps(ARC_WIDTH * 2)}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
    >
      {/* Defs */}
      <Gradients />

      {/* Main arc */}
      <g id="main-arc">
        <path
          id="main-arc-stroke"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 1.4}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-bg"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_BG}
          strokeWidth={ARC_WIDTH * 1.3}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-value"
          {...gauge.getArcProps({
            offset: 0,
            startAngle:
              END_ANGLE +
              START_ANGLE -
              gauge.valueToAngle(animatedFuelRate ?? 0),
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={`url(#${
            isFuelRateBad ? SvgGradients.Red : SvgGradients.Blue
          }`}
          strokeWidth={value > 0 ? ARC_WIDTH : 0}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Outer arc */}
      <g id="outer-arc">
        <path
          id="outer-arc-stroke"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.1,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 0.4}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="outer-arc-bg"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.1,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_BG}
          strokeWidth={ARC_WIDTH * 0.3}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="outer-arc-value"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.1,
            startAngle: START_ANGLE,
            endAngle: Math.min(END_ANGLE, START_ANGLE + END_ANGLE - limitAngle),
          })}
          fill="none"
          stroke={
            isFuelRateBad
              ? `url(#${SvgGradients.Red})`
              : `url(#${SvgGradients.RedFaded})`
          }
          strokeWidth={ARC_WIDTH * 0.3}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Center value and label */}
      <g id="center">
        <text
          x={"48%"}
          fill={isFuelRateBad ? `url(#${SvgGradients.Red})` : Color.White}
          className="text-[4.5rem] font-bold font-mono"
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="end"
        >
          {displayFuelRateNmi}
        </text>
        <text
          x={"46%"}
          y={"13%"}
          className="text-2xl font-semibold"
          fill={isFuelRateBad ? Color.Rose[600] : Color.Primary[500]}
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="end"
        >
          {label}
        </text>
      </g>
    </svg>
  );
};
